import { yupResolver } from '@hookform/resolvers/yup';
import { walletsInstallmentSchema } from '@payments-checkout/shared/helpers/constants/validation-input';
import { InstallmentsManager } from '@payments-checkout/shared/helpers/index';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import { Select, Text } from '@stone-payments/jade';
import { useEffect } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { usePageSharedData } from '../../../hooks/usePageSharedData';
import { useFormTypedValues } from '../../providers/formValuesProvider.provider';

export type WalletInstallments = {
  installments: string;
};

export const InstallmentsForm = () => {
  const intl = useIntl();

  const { setFormValues } = useFormTypedValues();

  const {
    installments,
    setInstallments,
    setInstallmentsExplanationText,
    setSurchargesText,
  } = usePaymentMethod();

  const { installmentOptions } = usePageSharedData();

  const formattedInstallmentsOptions = InstallmentsManager.formatOptions(installmentOptions, intl.formatMessage);

  const methods = useForm<WalletInstallments>({
    defaultValues: {
      installments: installments?.toString() || undefined,
    },
    resolver: yupResolver<WalletInstallments>(walletsInstallmentSchema),
  });

  useEffect(() => {
    if (installments) {
      methods.reset({
        installments: installments.toString(),
      });
    }
  }, [installments]);

  useEffect(() => {
    if (formattedInstallmentsOptions.length === 1) {
      const [onlyOption] = formattedInstallmentsOptions;
      methods.setValue('installments', onlyOption.value);
      InstallmentsManager.handleInstallmentsChange(
        onlyOption.value,
        formattedInstallmentsOptions,
        setInstallments,
        setInstallmentsExplanationText,
        setSurchargesText
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedInstallmentsOptions]);

  const handleInstallments = (e: React.ChangeEvent<HTMLSelectElement>) => InstallmentsManager.handleInstallmentsChange(
    e.target.value,
    formattedInstallmentsOptions,
    setInstallments,
    setInstallmentsExplanationText,
    setSurchargesText
  )

  const onSubmit: SubmitHandler<WalletInstallments> = (data) => {
    setFormValues((prev) => ({ ...prev, ...data }));
  };

  return (
    <FormProvider {...methods}>
      <form
        id="installments-form"
        onSubmit={methods.handleSubmit(onSubmit)}
        data-testid="checkout-installments-form"
        className="w-full gap-jade-200 flex flex-col pb-jade-200 pt-jade-200"
      >
        <Text variant="heading-small" color="high">
          {intl.formatMessage({ id: 'installmentsForm.title' })}
        </Text>
        <Select
          label={intl.formatMessage({ id: 'creditCardForm.installments' })}
          size="medium"
          placeholder={intl.formatMessage({
            id: 'creditCardForm.installmentsSelect.placeholder',
          })}
          options={formattedInstallmentsOptions}
          disabled={formattedInstallmentsOptions.length === 1}
          error={!!methods.formState.errors?.installments?.message}
          supportText={
            methods.formState.errors?.installments?.message &&
              intl.formatMessage({
                id: `creditCardFields.validate.${methods.formState.errors?.installments?.message}`,
          })}
          {...methods.register('installments', {
            onChange: (e) => handleInstallments(e)
          })}
        />
    </form>
    </FormProvider>
  );
};
